import { useCallback } from "react";

import { OrganizationCountry } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { getStripeInstance } from "@/common/helpers/stripe";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";

export const useUpdateCreditCard = () => {
  const { currentUser } = useCurrentUserContext();
  const { locale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const { mutateAsync: createStripeCheckoutSession } = trpc.createStripeCheckoutSessionForSubscription.useMutation();

  const updateCreditCard = useCallback(
    (subscriptionId: string, country: OrganizationCountry) => async () => {
      logAmplitudeEvent(AmplitudeEvents.DonorTransactionDrawerClickPaymentMethodUpdate);
      if (!currentUser) {
        return;
      }

      const stripe = await getStripeInstance(country, locale);
      if (!stripe || !currentUser.email) {
        return;
      }
      const { data: stripeSessionCreation } = await createStripeCheckoutSession({
        subscriptionId,
        email: currentUser.email,
        country,
      });
      if (!stripeSessionCreation?.id) {
        enqueueSnackbar(t("dashboard", "donorTransactionTable.errorOnSessionCreation"), { vibe: "danger" });
        return;
      }
      const { error } = await stripe.redirectToCheckout({
        sessionId: stripeSessionCreation.id,
      });
      if (error) {
        enqueueSnackbar(t("dashboard", "donorTransactionTable.errorOnCheckoutRedirection"), {
          vibe: "danger",
        });
      }
    },
    [logAmplitudeEvent, currentUser, locale, createStripeCheckoutSession, enqueueSnackbar, t]
  );

  return {
    updateCreditCard,
  };
};
